import ProductsApiService from "@/core/services/api.service.products";
import TopProductsCatalogApiService from "@/core/services/api.service.top-products-catalog";

export default {
  name: "create-top-product-popup",
  props: {
    value: {
      type: Boolean,
      default: false
    },
    itemSelectionGroups: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      step: 1,
      loading: false,
      // Step 1: Product Search
      products: [],
      totalProducts: 0,
      selectedProduct: null,
      searchFilters: {
        name: "",
        supplierCode: "",
        code: "",
        shopId: null
      },
      productPagination: {
        page: 1,
        itemsPerPage: 10
      },
      // Step 2: Selection Group
      selectedGroupId: null,
      showCreateGroupDialog: false,
      newGroupName: "",
      groupNameRules: [
        v => !!v || "Group name is required",
        v => (v && v.length >= 3) || "Group name must be at least 3 characters"
      ]
    };
  },
  async mounted() {
    await ProductsApiService.init();
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },
    productHeaders() {
      return [
        { text: "", value: "select", sortable: false, width: "5%" },
        { text: "ID", value: "id", sortable: false, width: "8%" },
        { text: "Image", value: "thumbPath1", sortable: false, width: "10%", align: "center" },
        { text: "Name", value: "name", sortable: false, width: "30%" },
        { text: "Shop ID", value: "shopId", sortable: false, width: "10%" },
        { text: "Price", value: "price", sortable: false, width: "12%", align: "center" },
        { text: "Stock", value: "inStock", sortable: false, width: "10%", align: "center" },
        { text: "Published", value: "published", sortable: false, width: "15%", align: "center" }
      ];
    },
    hasActiveFilters() {
      return !!(
        this.searchFilters.name ||
        this.searchFilters.supplierCode ||
        this.searchFilters.code ||
        this.searchFilters.shopId
      );
    },
    canProceedToStep2() {
      return this.selectedProduct !== null;
    },
    canSave() {
      return this.selectedProduct && this.selectedGroupId;
    }
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.resetDialog();
      }
    }
  },
  methods: {
    async searchProducts() {
      try {
        this.loading = true;
        const params = {
          page: this.productPagination.page - 1,
          size: this.productPagination.itemsPerPage
        };

        if (this.searchFilters.name) params.name = this.searchFilters.name;
        if (this.searchFilters.supplierCode) params.supplierCode = this.searchFilters.supplierCode;
        if (this.searchFilters.code) params.code = this.searchFilters.code;
        if (this.searchFilters.shopId) params.shopId = this.searchFilters.shopId;

        const response = await ProductsApiService.getAllProducts(params);
        this.products = response.data.items || [];
        this.totalProducts = response.data.totalNumOfItems || 0;
      } catch (error) {
        console.error("Error searching products:", error);
        this.$bvToast?.toast("Failed to search products", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      } finally {
        this.loading = false;
      }
    },

    clearFilters() {
      this.searchFilters = {
        name: "",
        supplierCode: "",
        code: "",
        shopId: null
      };
      this.products = [];
      this.totalProducts = 0;
      this.selectedProduct = null;
    },

    selectProduct(product) {
      this.selectedProduct = product;
    },

    goToStep2() {
      if (this.canProceedToStep2) {
        this.step = 2;
      }
    },

    goBackToStep1() {
      this.step = 1;
    },

    openCreateGroupDialog() {
      this.newGroupName = "";
      this.showCreateGroupDialog = true;
    },

    closeCreateGroupDialog() {
      this.showCreateGroupDialog = false;
      this.newGroupName = "";
    },

    async createNewGroup() {
      if (!this.$refs.createGroupForm.validate()) return;

      try {
        this.loading = true;
        const response = await TopProductsCatalogApiService.createItemSelectionGroup({
          name: this.newGroupName
        });

        this.$bvToast?.toast("Selection group created successfully", {
          variant: "success",
          solid: true,
          autoHideDelay: 3000
        });

        this.closeCreateGroupDialog();
        this.$emit("group-created", response.data);

        // Select the newly created group
        if (response.data && response.data.id) {
          this.selectedGroupId = response.data.id;
        }
      } catch (error) {
        console.error("Error creating selection group:", error);
        this.$bvToast?.toast("Failed to create selection group", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      } finally {
        this.loading = false;
      }
    },

    async saveProduct() {
      if (!this.canSave) return;

      try {
        this.loading = true;
        await TopProductsCatalogApiService.createItemSelection({
          itemId: this.selectedProduct.id,
          itemSelectionGroupId: this.selectedGroupId
        });

        this.$bvToast?.toast("Product added to Top Products Catalog successfully", {
          variant: "success",
          solid: true,
          autoHideDelay: 3000
        });

        this.closeDialog();
        this.$emit("product-added");
      } catch (error) {
        console.error("Error adding product:", error);
        this.$bvToast?.toast("Failed to add product to catalog", {
          variant: "danger",
          solid: true,
          autoHideDelay: 3000
        });
      } finally {
        this.loading = false;
      }
    },

    closeDialog() {
      this.show = false;
      this.resetDialog();
    },

    resetDialog() {
      this.step = 1;
      this.products = [];
      this.totalProducts = 0;
      this.selectedProduct = null;
      this.selectedGroupId = null;
      this.searchFilters = {
        name: "",
        supplierCode: "",
        code: "",
        shopId: null
      };
      this.productPagination = {
        page: 1,
        itemsPerPage: 10
      };
    }
  }
};
